import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { isEqual } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  selectAuthScheme,
  selectCurrentClinic,
  selectCurrentUserToken
} from "features/auth/auth-slice";
import {
  addChartNoteCompletionChunk,
  addTextTemplateCompletionChunk,
  resetChartNoteCompletionChunk,
  resetTextTemplateCompletionChunk
} from "features/notifications/notifications.slice";
import { googleReviewsApi } from "services/google-reviews";
import { extendedMessagesApi } from "services/messages";
import { MessageErrorReason } from "services/types/messages.type";
import { CSVReportResponse } from "services/types/reports.type";
import { getDynamicWsUrl } from "utils/api-config.util";
import { buildWebsocketAuthToken } from "utils/auth-header.util";
import type {
  NoahSubReportSyncState,
  NoahSyncState
} from "utils/noah-sync-storage.util";
import {
  readAndPruneNoahSyncEntries,
  removeNoahSyncState,
  writeNoahSyncState
} from "utils/noah-sync-storage.util";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import { NOAH_SYNC_ISSUE_FLAG } from "../constants/feature-flags";
import { extendedNoahApi } from "../services/noah";
import { reportsApi } from "../services/reports";

export const NotificationType = "notify.message";
export const ReportCompletionChunkType = "report.completion.chunk";
export const MessageCompletionChunkType = "message.completion.chunk";
export const ChartNoteCompletionChunkType = "chartnote.completion.chunk";
export const TextTemplateCompletionChunkType = "texttemplate.completion.chunk";
export const AppointmentStatusUpdateType = "appointment.status.update";
export const CSVCompletionNotificationType = "csv.completion.notification";
// Sent by the backend report task when a CSV fails. Not in SYSTEM_MESSAGE_TYPES: only callers
// that opt in through `useCsvReportHook({ trackErrors })` listen for it.
export const CSVErrorNotificationType = "csv.error.notification";
export const ExcelCompletionNotificationType = "xlsx.completion.notification";
export const NoahSessionUpdatedNotificationType =
  "noah.session.update.notification";
export const TaskCommentCreated = "stafftaskcomment.create.notification";
export const PaymentRequestCompletionNotificationType =
  "paymentrequest.completion.notification";
export const PatientSummaryCompletionNotificationType =
  "patient.summary.completion.notification";
export const DiagnosisGenerationStartedType = "diagnosis.generation.started";
export const DiagnosisGenerationCompletedType =
  "diagnosis.generation.completed";
export const DiagnosisGenerationFailedType = "diagnosis.generation.failed";
export const SummaryGenerationStartedType = "summary.generation.started";
export const SummaryGenerationCompletedType = "summary.generation.completed";
export const SummaryGenerationFailedType = "summary.generation.failed";
export const AmbientScribeSoapCompletionNotificationType =
  "ambientscribe.soap.completion.notification";
export const AmbientScribeSummaryCompletionNotificationType =
  "ambientscribe.summary.notification";
/**
 * A non-ChartNote generated note finished — succeeded OR failed (SN-19067, doc §8).
 *
 * Distinct from the SOAP message above, which asserts the chart note was updated; that is
 * untrue for this flow, which writes only to `ai_ambientscribe_notes`.
 */
export const AmbientScribeNoteNotificationType =
  "ambientscribe.note.notification";
export const NoahSyncStartedType = "noah.sync.started";
export const NoahSubReportSyncStartedType = "noah.sub_report.sync.started";
export const NoahSubReportSyncCompletedType = "noah.sub_report.sync.completed";
export const NoahSyncCompletedType = "noah.sync.completed";
export const NoahSyncFailedType = "noah.sync.failed";
export const BulkAssignTagsCompletedNotificationType =
  "bulk.assign.tags.completed.notification";
// One pair of frames for every Additional Bulk Action / Tag Manager run; the
// message names the target and status so the browser needs no type per verb.
export const BulkActionStartedNotificationType =
  "bulk.action.started.notification";
export const BulkActionCompletedNotificationType =
  "bulk.action.completed.notification";
export const NewGoogleReviewNotificationType = "new.google.review";

const SYSTEM_MESSAGE_TYPES = [
  TaskCommentCreated,
  CSVCompletionNotificationType,
  PaymentRequestCompletionNotificationType,
  ExcelCompletionNotificationType,
  PatientSummaryCompletionNotificationType
];

export interface MessagePayload {
  id: number;
  patient: number;
  created_at: string;
  type: number;
  direction: number;
  status: number;
  text: string;
  is_read: boolean;
  error_reason: MessageErrorReason | null;
}

export interface Notification {
  type: typeof NotificationType;
  message: MessagePayload;
  is_read: boolean;
}

export interface CSVCompletionNotification {
  type: typeof CSVCompletionNotificationType;
  message: CSVReportResponse;
}

export interface ReportCompletionChunk {
  type: typeof ReportCompletionChunkType;
  message: string;
}

export interface ReportCompletionChunk {
  type: typeof ReportCompletionChunkType;
  message: string;
}
export interface MessageCompletionChunk {
  type: typeof MessageCompletionChunkType;
  message: string;
}
export interface ChartNoteCompletionChunk {
  type: typeof ChartNoteCompletionChunkType;
  message: string;
}
export interface TextTemplateCompletionChunk {
  type: typeof TextTemplateCompletionChunkType;
  message: string;
}

export interface TextTemplateCompletionChunk {
  type: typeof TextTemplateCompletionChunkType;
  message: string;
}

export interface NoahSessionUpdatedNotification {
  type: typeof NoahSessionUpdatedNotificationType;
  message: { patient_id: number; report_id?: number };
}

export interface PaymentRequestCompletionNotification {
  id?: number;
  type: typeof PaymentRequestCompletionNotificationType;
  message: string;
}

export interface BulkAssignTagsCompletedNotification {
  type: typeof BulkAssignTagsCompletedNotificationType;
  message: { bulk_action_id: number; patients_count: number };
}

export interface BulkActionCompletedNotification {
  type: typeof BulkActionCompletedNotificationType;
  message: {
    bulk_action_id: number;
    bulk_action_target: number;
    status: "completed" | "failed" | "processing";
    patients_count?: number;
    updated_count?: number;
    removed_count?: number;
    archived_count?: number;
    restored_count?: number;
    skipped_count?: number;
    undo_of?: number;
    [key: string]: unknown;
  };
}

export interface PatientSummaryCompletionNotification {
  type: typeof PatientSummaryCompletionNotificationType;
  message: {
    patient: string;
    type: string;
    summary: string;
    created_by_id: string;
  };
}

export interface DiagnosisGenerationStartedNotification {
  type: typeof DiagnosisGenerationStartedType;
  message: { report_id: number };
}

export interface DiagnosisGenerationCompletedNotification {
  type: typeof DiagnosisGenerationCompletedType;
  message: { report_id: number };
}

export interface DiagnosisGenerationFailedNotification {
  type: typeof DiagnosisGenerationFailedType;
  message: { report_id: number };
}

export interface SummaryGenerationStartedNotification {
  type: typeof SummaryGenerationStartedType;
  message: { report_id: number };
}

export interface SummaryGenerationCompletedNotification {
  type: typeof SummaryGenerationCompletedType;
  message: { report_id: number };
}

export interface SummaryGenerationFailedNotification {
  type: typeof SummaryGenerationFailedType;
  message: { report_id: number };
}

export interface NoahSyncStartedNotification {
  type: typeof NoahSyncStartedType;
  message: { report_id: number };
}

export interface NoahSubReportSyncStartedNotification {
  type: typeof NoahSubReportSyncStartedType;
  message: { report_id: number; sub_report_type: string };
}

export interface NoahSubReportSyncCompletedNotification {
  type: typeof NoahSubReportSyncCompletedType;
  message: { report_id: number; sub_report_type: string };
}

export interface NoahSyncCompletedNotification {
  type: typeof NoahSyncCompletedType;
  message: { report_id: number; total_sub_reports: number };
}

export interface NoahSyncFailedNotification {
  type: typeof NoahSyncFailedType;
  message: { report_id: number; error: string };
}

interface GeneratingSummaries {
  [patientId: string]: {
    [type: string]: boolean;
  };
}

interface DiagnosisGenerationState {
  [reportId: number]: {
    startedAt: number;
    timeoutId?: NodeJS.Timeout;
  };
}

interface SummaryGenerationState {
  [reportId: number]: {
    startedAt: number;
    timeoutId?: NodeJS.Timeout;
  };
}

export type { NoahSubReportSyncState, NoahSyncState };

const CONNECTION_STATUSES = {
  [ReadyState.CONNECTING]: "Connecting",
  [ReadyState.OPEN]: "Open",
  [ReadyState.CLOSING]: "Closing",
  [ReadyState.CLOSED]: "Closed",
  [ReadyState.UNINSTANTIATED]: "Uninstantiated"
};

const clinicNotificationsStorage = setupLocalStorageAPI("clinic-notifications");

interface ClinicNotificationsContextType {
  messages: Notification[];
  setMessages: React.Dispatch<React.SetStateAction<Notification[]>>;
  notifications: MessagePayload[];
  systemMessages: any[];
  readyState: ReadyState;
  connectionStatus: string;
  removeNotification: (id: number) => void;
  readNotification: (id: number) => void;
  readNotifications: (ids: number[]) => void;
  readAllNotifications: () => void;
  reportCompletionChunk: ReportCompletionChunk[];
  messageCompletionChunk: MessageCompletionChunk[];
  clearCompletionChunks: () => void;
  addMessageHandlers: (handler: any, key: string) => void;
  removeMessageHandlers: (key: string) => void;
  /**
   * Like addMessageHandlers, but called once for EVERY socket frame. addMessageHandlers sees
   * `lastJsonMessage`, which keeps only the latest of several messages that arrive before React
   * renders, and skips a message equal to the previous one — fine for "something changed",
   * lossy for "what changed" (ENG-2225).
   */
  addRawMessageHandler: (handler: (message: any) => void, key: string) => void;
  removeRawMessageHandler: (key: string) => void;
  noahNotification: NoahSessionUpdatedNotification["message"] | null;
  setNoahNotification: React.Dispatch<
    React.SetStateAction<NoahSessionUpdatedNotification["message"] | null>
  >;
  patientSummaryNotification:
    | PatientSummaryCompletionNotification["message"]
    | null;
  setPatientSummaryNotification: React.Dispatch<
    React.SetStateAction<PatientSummaryCompletionNotification["message"] | null>
  >;
  generatingSummaries: GeneratingSummaries;
  setGeneratingSummary: (
    patientId: string,
    type: string,
    isGenerating: boolean
  ) => void;
  isDiagnosisGenerating: (reportId: number) => boolean;
  isSummaryGenerating: (reportId: number) => boolean;
  noahSyncState: NoahSyncState;
  setNoahSyncState: React.Dispatch<React.SetStateAction<NoahSyncState>>;
  isNoahSyncing: (reportId: number) => boolean;
  getNoahSubReportSyncState: (
    reportId: number,
    subReportType: string
  ) => NoahSubReportSyncState | undefined;
  clearNoahSyncState: (reportId: number) => void;
}

const ClinicNotificationsContext =
  React.createContext<ClinicNotificationsContextType | null>(null);

type PatientsListProviderProps = {
  children?: React.ReactNode;
};

export function ClinicNotificationsProvider({
  children
}: PatientsListProviderProps) {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const isNoahSyncIssueFlagEnabled = flags[NOAH_SYNC_ISSUE_FLAG];
  const token = useAppSelector(selectCurrentUserToken);
  const authScheme = useAppSelector(selectAuthScheme);
  const currentClinic = useAppSelector(selectCurrentClinic);

  const [socketUrl, setSocketUrl] = useState("");
  const [systemMessages, setSystemMessages] = useState<any[]>([]);
  const [messages, setMessages] = useState<Notification[]>([]);
  const onMessageReceivedHandlers = useRef<Record<string, any>>({});
  const rawMessageHandlers = useRef<Record<string, (message: any) => void>>({});
  const [lastProcessedMessage, setLastProcessedMessage] = useState<any>(null);
  const defaultNotifications = clinicNotificationsStorage.getData([]);
  const [notifications, setNotifications] =
    useState<MessagePayload[]>(defaultNotifications);
  const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } =
    useWebSocket<Notification | any>(
      socketUrl,
      {
        shouldReconnect: () => true,
        onMessage: (message) => {
          const handlers = Object.values(rawMessageHandlers.current);
          if (handlers.length === 0 || !message?.data) {
            return;
          }
          let data: unknown;
          try {
            data = JSON.parse(message.data);
          } catch {
            return;
          }
          // onMessage runs before react-use-websocket stores the frame, so a throwing handler
          // would cost every lastJsonMessage consumer this frame.
          handlers.forEach((handler) => {
            try {
              handler(data);
            } catch (error) {
              console.error("Raw clinic socket handler failed", error);
            }
          });
        },
        filter: (message) => {
          const data =
            message && message.data ? JSON.parse(message.data) : null;
          return !(
            isNoahSyncIssueFlagEnabled &&
            data &&
            data.type === NoahSessionUpdatedNotificationType
          );
        }
      },
      Boolean(socketUrl)
    );

  const {
    reportCompletionChunk,
    messageCompletionChunk,
    clearCompletionChunks,
    noahNotification,
    setNoahNotification,
    patientSummaryNotification,
    setPatientSummaryNotification,
    diagnosisGenerationState,
    summaryGenerationState,
    noahSyncState,
    setNoahSyncState,
    clearNoahSyncState
  } = useCompletionChunk(lastJsonMessage);

  const [generatingSummaries, setGeneratingSummaries] =
    useState<GeneratingSummaries>({});

  const setGeneratingSummary = useCallback(
    (patientId: string, type: string, isGenerating: boolean) => {
      setGeneratingSummaries((prev) => ({
        ...prev,
        [patientId]: {
          ...prev[patientId],
          [type]: isGenerating
        }
      }));
    },
    []
  );

  const isDiagnosisGenerating = useCallback(
    (reportId: number) => {
      const state = diagnosisGenerationState[reportId];
      if (!state) return false;
      const elapsed = Date.now() - state.startedAt;
      return elapsed < 60_000;
    },
    [diagnosisGenerationState]
  );

  const isSummaryGenerating = useCallback(
    (reportId: number) => {
      const state = summaryGenerationState[reportId];
      if (!state) return false;
      const elapsed = Date.now() - state.startedAt;
      return elapsed < 60_000;
    },
    [summaryGenerationState]
  );

  const isNoahSyncing = useCallback(
    (reportId: number) => {
      const state = noahSyncState[reportId];
      if (!state) return false;
      return state.status === "syncing";
    },
    [noahSyncState]
  );

  const getNoahSubReportSyncState = useCallback(
    (reportId: number, subReportType: string) => {
      const state = noahSyncState[reportId];
      if (!state) return undefined;
      return state.subReports[subReportType];
    },
    [noahSyncState]
  );

  const clearNoahSyncStateCallback = useCallback(
    (reportId: number) => {
      clearNoahSyncState(reportId);
    },
    [clearNoahSyncState]
  );

  useEffect(() => {
    if (currentClinic?.id) {
      setSocketUrl(
        `${getDynamicWsUrl()}/clinic-notifications/${currentClinic?.id}/`
      );
    }
  }, [currentClinic?.id]);
  useEffect(() => {
    if (!isEqual(lastJsonMessage, lastProcessedMessage)) {
      Object.values(onMessageReceivedHandlers.current).map(
        (handler) => handler && handler(lastJsonMessage)
      );
    }
  }, [lastJsonMessage, onMessageReceivedHandlers, lastProcessedMessage]);
  useEffect(() => {
    if (lastJsonMessage !== null) {
      if (lastJsonMessage.type === NotificationType) {
        setMessages((prev) =>
          prev.concat({
            type: lastJsonMessage.type,
            message: lastJsonMessage.message,
            is_read: false
          })
        );
        if (lastJsonMessage.message.is_read === false) {
          const current = clinicNotificationsStorage.getData([]);
          clinicNotificationsStorage.updateData([
            ...current,
            lastJsonMessage.message
          ]);
          setNotifications((prev) => prev.concat(lastJsonMessage.message));
        }
        // Invalidate unread messages count to trigger refetch
        dispatch(
          extendedMessagesApi.util.invalidateTags(["UnreadMessagesCount"])
        );
      } else if (lastJsonMessage.type === NewGoogleReviewNotificationType) {
        const { star_rating, reviewer_name } = lastJsonMessage.message ?? {};
        if (star_rating && reviewer_name) {
          dispatch(
            setCustomMessage(
              `New ${star_rating}-star Google review from ${reviewer_name}`
            )
          );
          dispatch(setOpen(true));
        }
        dispatch(
          googleReviewsApi.util.invalidateTags([
            "GoogleReviews",
            "GoogleReviewSummary",
            "GoogleReviewConnections"
          ])
        );
      } else if (SYSTEM_MESSAGE_TYPES.includes(lastJsonMessage.type)) {
        setSystemMessages((prev) => prev.concat(lastJsonMessage));
      }
      setLastProcessedMessage(lastJsonMessage);
    }
  }, [lastJsonMessage, setSystemMessages, setLastProcessedMessage, dispatch]);

  useEffect(() => {
    const ws = getWebSocket();
    if (
      readyState === ReadyState.OPEN &&
      token &&
      socketUrl &&
      ws?.readyState === WebSocket.OPEN
    ) {
      try {
        sendJsonMessage({
          method: "authenticate",
          token: buildWebsocketAuthToken(token, authScheme)
        });
      } catch (error) {
        console.error(
          "[useClinicNotifications] Failed to send auth message:",
          error
        );
      }
    }
  }, [readyState, sendJsonMessage, token, authScheme, socketUrl, getWebSocket]);

  const removeNotification = useCallback(
    (id: number) => {
      const index = notifications.findIndex(
        (notification) => notification.id === id
      );
      if (index !== -1) {
        const newNotifications = [...notifications];
        newNotifications.splice(index, 1);
        setNotifications(newNotifications);
        clinicNotificationsStorage.updateData(newNotifications);
      }
    },
    [notifications]
  );
  const readNotification = useCallback(
    (id: number) => {
      const newNotifications = notifications.map((notification) => ({
        ...notification,
        is_read: notification.id === id ? true : notification.is_read
      }));
      setNotifications(newNotifications);
      clinicNotificationsStorage.updateData(newNotifications);
    },
    [notifications]
  );
  const readNotifications = useCallback(
    (ids: number[]) => {
      const newNotifications = notifications.map((notification) => ({
        ...notification,
        is_read: ids.includes(notification.id) ? true : notification.is_read
      }));
      setNotifications(newNotifications);
      clinicNotificationsStorage.updateData(newNotifications);
    },
    [notifications]
  );
  const readAllNotifications = useCallback(() => {
    const newNotifications = notifications.map((notification) => ({
      ...notification,
      is_read: true
    }));
    setNotifications(newNotifications);
    clinicNotificationsStorage.updateData(newNotifications);
  }, [notifications]);
  const connectionStatus = CONNECTION_STATUSES[readyState];

  const addMessageHandlers = useCallback(
    (handler: any, key: string) => {
      onMessageReceivedHandlers.current[key] = handler;
    },
    [onMessageReceivedHandlers]
  );

  const removeMessageHandlers = useCallback((key: string) => {
    delete onMessageReceivedHandlers.current[key];
  }, []);

  const addRawMessageHandler = useCallback(
    (handler: (message: any) => void, key: string) => {
      rawMessageHandlers.current[key] = handler;
    },
    []
  );

  const removeRawMessageHandler = useCallback((key: string) => {
    delete rawMessageHandlers.current[key];
  }, []);

  useEffect(() => {
    if (lastJsonMessage?.type === PatientSummaryCompletionNotificationType) {
      const patientId = lastJsonMessage.message.patient_id;
      const type = lastJsonMessage.message.summary_type;

      setGeneratingSummary(patientId, type, false);
    }
  }, [lastJsonMessage, setGeneratingSummary]);

  const contextValue = useMemo(
    () => ({
      messages,
      setMessages,
      notifications,
      systemMessages,
      readyState,
      connectionStatus,
      removeNotification,
      readNotification,
      readNotifications,
      readAllNotifications,
      reportCompletionChunk,
      messageCompletionChunk,
      clearCompletionChunks,
      addMessageHandlers,
      removeMessageHandlers,
      addRawMessageHandler,
      removeRawMessageHandler,
      noahNotification,
      setNoahNotification,
      patientSummaryNotification,
      setPatientSummaryNotification,
      generatingSummaries,
      setGeneratingSummary,
      isDiagnosisGenerating,
      isSummaryGenerating,
      noahSyncState,
      setNoahSyncState,
      isNoahSyncing,
      getNoahSubReportSyncState,
      clearNoahSyncState: clearNoahSyncStateCallback
    }),
    [
      messages,
      setMessages,
      notifications,
      systemMessages,
      readyState,
      connectionStatus,
      removeNotification,
      readNotification,
      readNotifications,
      readAllNotifications,
      reportCompletionChunk,
      messageCompletionChunk,
      clearCompletionChunks,
      addMessageHandlers,
      removeMessageHandlers,
      addRawMessageHandler,
      removeRawMessageHandler,
      noahNotification,
      setNoahNotification,
      patientSummaryNotification,
      setPatientSummaryNotification,
      generatingSummaries,
      setGeneratingSummary,
      isDiagnosisGenerating,
      isSummaryGenerating,
      noahSyncState,
      setNoahSyncState,
      isNoahSyncing,
      getNoahSubReportSyncState,
      clearNoahSyncStateCallback
    ]
  );

  return (
    <ClinicNotificationsContext.Provider value={contextValue}>
      {children}
    </ClinicNotificationsContext.Provider>
  );
}

export default function useClinicNotifications() {
  const clinicNotificationsContext = useContext(ClinicNotificationsContext);
  if (!clinicNotificationsContext) {
    throw new Error(
      `useClinicNotifications must be used within a ClinicNotificationsContext`
    );
  }
  const {
    messages,
    setMessages,
    notifications,
    systemMessages,
    readyState,
    removeNotification,
    readAllNotifications,
    reportCompletionChunk,
    messageCompletionChunk,
    clearCompletionChunks,
    addMessageHandlers,
    removeMessageHandlers,
    addRawMessageHandler,
    removeRawMessageHandler,
    noahNotification,
    setNoahNotification,
    patientSummaryNotification,
    setPatientSummaryNotification,
    generatingSummaries,
    setGeneratingSummary,
    isDiagnosisGenerating,
    isSummaryGenerating,
    noahSyncState,
    setNoahSyncState,
    isNoahSyncing,
    getNoahSubReportSyncState,
    clearNoahSyncState: clearNoahSyncStateCallback
  } = clinicNotificationsContext;
  return {
    messages,
    setMessages,
    notifications,
    systemMessages,
    readyState,
    removeNotification,
    readAllNotifications,
    reportCompletionChunk,
    messageCompletionChunk,
    clearCompletionChunks,
    addMessageHandlers,
    removeMessageHandlers,
    addRawMessageHandler,
    removeRawMessageHandler,
    noahNotification,
    setNoahNotification,
    patientSummaryNotification,
    setPatientSummaryNotification,
    generatingSummaries,
    setGeneratingSummary,
    isDiagnosisGenerating,
    isSummaryGenerating,
    noahSyncState,
    setNoahSyncState,
    isNoahSyncing,
    getNoahSubReportSyncState,
    clearNoahSyncState: clearNoahSyncStateCallback
  };
}

function useCompletionChunk(lastJsonMessage: any) {
  const dispatch = useAppDispatch();

  //TODO => move state chunks to redux notifications slice state

  const [reportCompletionChunk, setReportCompletionChunk] = useState<
    ReportCompletionChunk[]
  >([]);
  const [messageCompletionChunk, setMessageCompletionChunk] = useState<
    MessageCompletionChunk[]
  >([]);
  const [patientSummaryNotification, setPatientSummaryNotification] = useState<
    PatientSummaryCompletionNotification["message"] | null
  >(null);

  const [noahNotification, setNoahNotification] = useState<
    NoahSessionUpdatedNotification["message"] | null
  >(null);

  const [diagnosisGenerationState, setDiagnosisGenerationState] =
    useState<DiagnosisGenerationState>({});
  const [summaryGenerationState, setSummaryGenerationState] =
    useState<SummaryGenerationState>({});
  const [noahSyncState, setNoahSyncState] = useState<
    Record<number, NoahSyncState>
  >({});

  // Initialize NOAH sync state from localStorage. This also self-heals a
  // browser that previously hit its localStorage quota, since stale and
  // corrupt entries are pruned as part of the read (ENG-1796).
  useEffect(() => {
    setNoahSyncState((prev) => ({
      ...prev,
      ...readAndPruneNoahSyncEntries()
    }));
  }, []);

  const clearNoahSyncState = useCallback((reportId: number) => {
    removeNoahSyncState(reportId);
    setNoahSyncState((prev) => {
      const updated = { ...prev };
      delete updated[reportId];
      return updated;
    });
  }, []);

  const persistNoahSyncState = useCallback(
    (reportId: number, state: NoahSyncState) => {
      // Never throws - a write failure degrades to in-memory-only state
      // for this entry instead of crashing the app.
      writeNoahSyncState(reportId, state);
      setNoahSyncState((prev) => ({
        ...prev,
        [reportId]: state
      }));
    },
    []
  );

  const clearCompletionChunks = useCallback(() => {
    setReportCompletionChunk([]);
    setMessageCompletionChunk([]);
    dispatch(resetChartNoteCompletionChunk());
    dispatch(resetTextTemplateCompletionChunk());
  }, [dispatch]);

  useEffect(() => {
    if (lastJsonMessage !== null) {
      switch (lastJsonMessage.type) {
        case ReportCompletionChunkType:
          setReportCompletionChunk((prev) =>
            prev.concat({
              type: lastJsonMessage.type,
              message: lastJsonMessage.message
            })
          );
          break;
        case MessageCompletionChunkType:
          setMessageCompletionChunk((prev) =>
            prev.concat({
              type: lastJsonMessage.type,
              message: lastJsonMessage.message
            })
          );
          break;
        case ChartNoteCompletionChunkType:
          dispatch(addChartNoteCompletionChunk(lastJsonMessage));
          break;
        case TextTemplateCompletionChunkType:
          dispatch(addTextTemplateCompletionChunk(lastJsonMessage));
          break;
        case PatientSummaryCompletionNotificationType:
          setPatientSummaryNotification(lastJsonMessage.message);
          break;
        case NoahSessionUpdatedNotificationType:
          const patientId = lastJsonMessage.message?.patient_id;
          const reportId = lastJsonMessage.message?.report_id;

          setNoahNotification(lastJsonMessage.message);
          dispatch(
            extendedNoahApi.util.invalidateTags([
              { id: patientId, type: "NoahSessions" }
            ])
          );

          dispatch(
            reportsApi.util.invalidateTags([{ type: "Reports", id: patientId }])
          );

          if (reportId) {
            dispatch(
              reportsApi.util.invalidateTags([{ type: "Report", id: reportId }])
            );
          }
          break;
        case DiagnosisGenerationStartedType:
          const startedReportId = lastJsonMessage.message?.report_id;
          if (startedReportId) {
            setDiagnosisGenerationState((prev) => {
              const existing = prev[startedReportId];
              if (existing?.timeoutId) {
                clearTimeout(existing.timeoutId);
              }

              const timeoutId = setTimeout(() => {
                setDiagnosisGenerationState((current) => {
                  const updated = { ...current };
                  delete updated[startedReportId];
                  return updated;
                });
              }, 60000);

              return {
                ...prev,
                [startedReportId]: {
                  startedAt: Date.now(),
                  timeoutId
                }
              };
            });
          }
          break;
        case DiagnosisGenerationCompletedType:
        case DiagnosisGenerationFailedType:
          const finallyReportId = lastJsonMessage.message?.report_id;
          if (finallyReportId) {
            setDiagnosisGenerationState((prev) => {
              const existing = prev[finallyReportId];
              if (existing?.timeoutId) {
                clearTimeout(existing.timeoutId);
              }
              const updated = { ...prev };
              delete updated[finallyReportId];
              return updated;
            });
            dispatch(
              reportsApi.util.invalidateTags([
                { type: "Report", id: finallyReportId }
              ])
            );
          }
          break;
        case SummaryGenerationStartedType:
          const startedSummaryReportId = lastJsonMessage.message?.report_id;
          if (startedSummaryReportId) {
            setSummaryGenerationState((prev) => {
              const existing = prev[startedSummaryReportId];
              if (existing?.timeoutId) {
                clearTimeout(existing.timeoutId);
              }

              const timeoutId = setTimeout(() => {
                setSummaryGenerationState((current) => {
                  const updated = { ...current };
                  delete updated[startedSummaryReportId];
                  return updated;
                });
              }, 60000);

              return {
                ...prev,
                [startedSummaryReportId]: {
                  startedAt: Date.now(),
                  timeoutId
                }
              };
            });
          }
          break;
        case SummaryGenerationCompletedType:
        case SummaryGenerationFailedType:
          const finallySummaryReportId = lastJsonMessage.message?.report_id;
          if (finallySummaryReportId) {
            setSummaryGenerationState((prev) => {
              const existing = prev[finallySummaryReportId];
              if (existing?.timeoutId) {
                clearTimeout(existing.timeoutId);
              }
              const updated = { ...prev };
              delete updated[finallySummaryReportId];
              return updated;
            });
            dispatch(
              reportsApi.util.invalidateTags([
                { type: "Report", id: finallySummaryReportId }
              ])
            );
          }
          break;
        case NoahSyncStartedType:
          const noahSyncReportId = lastJsonMessage.message?.report_id;
          if (noahSyncReportId) {
            const newSyncState: NoahSyncState = {
              status: "syncing",
              startedAt: Date.now(),
              subReports: {}
            };
            persistNoahSyncState(noahSyncReportId, newSyncState);
          }
          break;
        case NoahSubReportSyncStartedType:
          const noahSubReportStartReportId = lastJsonMessage.message?.report_id;
          const noahSubReportStartType =
            lastJsonMessage.message?.sub_report_type;
          if (noahSubReportStartReportId && noahSubReportStartType) {
            setNoahSyncState((prev) => {
              const currentState = prev[noahSubReportStartReportId];
              const updatedSubReports = {
                ...(currentState?.subReports || {}),
                [noahSubReportStartType]: {
                  status: "syncing",
                  startedAt: Date.now()
                }
              };
              const updatedState: NoahSyncState = {
                ...(currentState || { status: "syncing", subReports: {} }),
                subReports: updatedSubReports
              };
              persistNoahSyncState(noahSubReportStartReportId, updatedState);
              return prev;
            });
          }
          break;
        case NoahSubReportSyncCompletedType:
          const noahSubReportCompleteReportId =
            lastJsonMessage.message?.report_id;
          const noahSubReportCompleteType =
            lastJsonMessage.message?.sub_report_type;
          if (noahSubReportCompleteReportId && noahSubReportCompleteType) {
            setNoahSyncState((prev) => {
              const currentState = prev[noahSubReportCompleteReportId];
              if (!currentState) return prev;
              const updatedSubReports = {
                ...currentState.subReports,
                [noahSubReportCompleteType]: {
                  status: "completed",
                  startedAt:
                    currentState.subReports[noahSubReportCompleteType]
                      ?.startedAt || Date.now(),
                  completedAt: Date.now()
                }
              };
              const updatedState: NoahSyncState = {
                ...currentState,
                subReports: updatedSubReports
              };
              persistNoahSyncState(noahSubReportCompleteReportId, updatedState);
              return prev;
            });
            // Invalidate report cache to trigger refetch
            dispatch(
              reportsApi.util.invalidateTags([
                { type: "Report", id: noahSubReportCompleteReportId }
              ])
            );
          }
          break;
        case NoahSyncCompletedType:
          const noahSyncCompletedReportId = lastJsonMessage.message?.report_id;
          const noahTotalSubReports =
            lastJsonMessage.message?.total_sub_reports;
          if (noahSyncCompletedReportId) {
            setNoahSyncState((prev) => {
              const currentState = prev[noahSyncCompletedReportId];
              if (!currentState) return prev;
              const updatedState: NoahSyncState = {
                ...currentState,
                status: "completed",
                completedAt: Date.now(),
                totalSubReports: noahTotalSubReports
              };
              persistNoahSyncState(noahSyncCompletedReportId, updatedState);
              return prev;
            });
            dispatch(
              reportsApi.util.invalidateTags([
                { type: "Report", id: noahSyncCompletedReportId }
              ])
            );
          }
          break;
        case NoahSyncFailedType:
          const noahSyncFailedReportId = lastJsonMessage.message?.report_id;
          const noahSyncError = lastJsonMessage.message?.error;
          if (noahSyncFailedReportId) {
            setNoahSyncState((prev) => {
              const currentState = prev[noahSyncFailedReportId];
              const updatedState: NoahSyncState = {
                ...(currentState || { status: "syncing", subReports: {} }),
                status: "failed",
                completedAt: Date.now(),
                error: noahSyncError || "Unknown error"
              };
              persistNoahSyncState(noahSyncFailedReportId, updatedState);
              return prev;
            });
          }
          break;
        default:
          break;
      }
    }
  }, [lastJsonMessage, dispatch, persistNoahSyncState]);

  return {
    reportCompletionChunk,
    messageCompletionChunk,
    clearCompletionChunks,
    noahNotification,
    setNoahNotification,
    patientSummaryNotification,
    setPatientSummaryNotification,
    diagnosisGenerationState,
    setDiagnosisGenerationState,
    summaryGenerationState,
    setSummaryGenerationState,
    noahSyncState,
    setNoahSyncState,
    clearNoahSyncState
  };
}
